import { baseApi } from "../baseApi";
import { pick } from "../util";
import type { Supplier, CreateSupplierInput, UpdateSupplierInput } from "../types";

const SUPPLIER_FIELDS = `
  id
  organizationId
  name
  code
  email
  phone
  gstin
  contactPerson
  addressLine1
  addressLine2
  city
  state
  country
  postalCode
  leadTimeDays
  status
  createdAt
  updatedAt
`;

export const supplierApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSuppliers: builder.query<Supplier[], string | void>({
      query: (organizationId) => ({
        body: {
          query: `
            query Suppliers($organizationId: String) {
              suppliers(organizationId: $organizationId) {
                ${SUPPLIER_FIELDS}
              }
            }
          `,
          variables: { organizationId: organizationId ?? null },
        },
      }),

      transformResponse: (res: unknown) => pick<Supplier[]>(res, "suppliers"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((s) => ({ type: "Supplier" as const, id: s.id })),
              { type: "Supplier" as const, id: "LIST" },
            ]
          : [{ type: "Supplier" as const, id: "LIST" }],
    }),

    getSupplier: builder.query<Supplier, string>({
      query: (id) => ({
        body: {
          query: `
            query Supplier($id: String!) {
              supplier(id: $id) {
                ${SUPPLIER_FIELDS}
              }
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<Supplier>(res, "supplier"),

      providesTags: (_result, _err, id) => [{ type: "Supplier", id }],
    }),

    createSupplier: builder.mutation<Supplier, CreateSupplierInput>({
      query: (input) => ({
        body: {
          query: `
            mutation CreateSupplier($input: CreateSupplierInput!) {
              createSupplier(input: $input) {
                ${SUPPLIER_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) => pick<Supplier>(res, "createSupplier"),

      invalidatesTags: [{ type: "Supplier", id: "LIST" }],
    }),

    updateSupplier: builder.mutation<
      Supplier,
      { id: string; input: UpdateSupplierInput }
    >({
      query: ({ id, input }) => ({
        body: {
          query: `
            mutation UpdateSupplier($id: String!, $input: UpdateSupplierInput!) {
              updateSupplier(id: $id, input: $input) {
                ${SUPPLIER_FIELDS}
              }
            }
          `,
          variables: { id, input },
        },
      }),

      transformResponse: (res: unknown) => pick<Supplier>(res, "updateSupplier"),

      invalidatesTags: (_result, _err, { id }) => [
        { type: "Supplier", id },
        { type: "Supplier", id: "LIST" },
      ],
    }),

    deleteSupplier: builder.mutation<boolean, string>({
      query: (id) => ({
        body: {
          query: `
            mutation DeleteSupplier($id: String!) {
              deleteSupplier(id: $id)
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "deleteSupplier"),

      invalidatesTags: (_result, _err, id) => [
        { type: "Supplier", id },
        { type: "Supplier", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetSuppliersQuery,
  useLazyGetSuppliersQuery,
  useGetSupplierQuery,
  useLazyGetSupplierQuery,
  useCreateSupplierMutation,
  useUpdateSupplierMutation,
  useDeleteSupplierMutation,
} = supplierApi;
